/**
 *
 * ResetPassword
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useLocation } from 'react-router-dom';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  flushStateResetPassword,
  loadResetPassword,
  changeResetPassword,
  deleteResetPassword,
} from './actions';

import {
  selectResetPasswordData,
  selectResetPasswordList,
  selectResetPasswordLoading,
  selectResetPasswordError,
  selectResetPasswordDeleteSuccessful,
} from './selectors';

// antd component
import { Alert, Button, Form, Input } from 'antd';

// assets
import logoLogin from 'images/logo-name.svg';

// utils
import api, { setAuthorizationHeader } from 'utils/apis/axiosAPI';

function ResetPassword({
  // main data
  resetPasswordList,
  resetPasswordData,
  // states
  deleteSuccessful,
  error,
  loading,
  // actions
  loadResetPassword,
  changeResetPassword,
  flushState,
}) {
  useInjectReducer({ key: 'resetPassword', reducer });
  useInjectSaga({ key: 'resetPassword', saga });

  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [isForget, setIsForget] = useState(true);
  const [showSuccessForgot, setShowSuccessForgot] = useState(false);
  const [showErrorForgot, setShowErrorForgot] = useState(false);
  const [showSuccessReset, setShowSuccessReset] = useState(false);
  const [showErrorReset, setShowErrorReset] = useState(false);
  const urlParams = new URLSearchParams(location.search);

  setAuthorizationHeader();

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // --> main code here
    if(urlParams.get('id')){
      setIsForget(false);
    }
    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
    };
  }, []);

  const onFinishForgot = values => {
    setIsLoading(true);
    api.auth.forgotPassword(values).then(result => {
      setShowSuccessForgot(true);
      setShowErrorForgot(false);
      setIsLoading(false);
    }).catch(err => {
      console.error(err);
      setShowSuccessForgot(false);
      setShowErrorForgot(true);
      setIsLoading(false);
    })
  };

  const onFinishFailedForgot = errorInfo => {
    console.error('Failed ResetPassword:', errorInfo);
  };

  const onFinishReset = values => {
    setIsLoading(true);
    values.token = urlParams.get('id');
    api.auth.resetPassword(values).then(result => {
      setShowSuccessReset(true);
      setShowErrorReset(false);
      setIsLoading(false);
    }).catch(err => {
      console.error(err);
      setShowSuccessReset(false);
      setShowErrorReset(true);
      setIsLoading(false);
    })
  };

  const onFinishFailedReset = errorInfo => {
    console.error('Failed ResetPassword:', errorInfo);
  };

  // render function
  return (
    <>
      <div className={'login-page'}>
          <div className="login-form__column">
            <div className="login-form__header">
              <img
                src={logoLogin}
                className="logo-login"
                alt="BeeMed Logo"
              />
              {isForget &&
              <Form
                name="basic"
                className="login-form__container"
                initialValues={{ remember: true }}
                onFinish={value => onFinishForgot(value)}
                onFinishFailed={errorInfo => onFinishFailedForgot(errorInfo)}
              >
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: `Veuillez saisir votre adresse e-mail`,
                    },
                  ]}
                >
                  <Input size="large" placeholder="Adresse e-mail" />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="login-submit"
                    loading={loading}
                  >
                    Réinitialiser mot de passe
                  </Button>
                </Form.Item>
                {showErrorForgot && (
                  <Alert
                    message="Erreur de connexion"
                    description={`Une erreur de connexion est survenue. Verifiez vos informations de connexion (Adresse e-mail et/ou Mot de passe).`}
                    type="error"
                    closable
                  />
                )}
                {showSuccessForgot && (
                  <Alert
                    message="Mail envoyé avec succès"
                    description={`Une demande de réinitialisation vous a été envoyée par mail`}
                    type="success"
                    closable
                  />
                )}
              </Form>
              }
              {!isForget &&
              <Form
                name="basic"
                className="login-form__container"
                initialValues={{ remember: true }}
                onFinish={value => onFinishReset(value)}
                onFinishFailed={errorInfo => onFinishFailedReset(errorInfo)}
              >
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: 'Veuillez saisir votre mot de passe',
                    },
                  ]}
                  hasFeedback
                >
                  <Input.Password placeholder={"Mot de passe"} size={'large'} />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  dependencies={['password']}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: 'Veuillez saisir votre mot de passe',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Les mots de passe doivent être similaires.'));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder={"Mot de passe"} size={'large'}  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="login-submit"
                    loading={isLoading}
                  >
                    Sauvegarder
                  </Button>
                </Form.Item>
                {showErrorReset && (
                  <Alert
                    message="Erreur de connexion"
                    description={`Une erreur de connexion est survenue.`}
                    type="error"
                    closable
                  />
                )}
                {showSuccessReset && (
                  <Alert
                    message="Mot de passe modifié"
                    description={`Votre mot de passe a été modifié avec succès.`}
                    type="success"
                    closable
                  />
                )}
              </Form>
              }
            </div>
        </div>
        <svg
                className="waves"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="0 24 150 28"
                preserveAspectRatio="none"
                shapeRendering="auto"
              >
                <defs>
                  <path
                    id="gentle-wave"
                    d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"
                  />
                </defs>
                <g className="parallax">
                  <use
                    href="#gentle-wave"
                    x="48"
                    y="0"
                    fill="rgba(255,255,255,0.7"
                  />
                  <use
                    href="#gentle-wave"
                    x="48"
                    y="3"
                    fill="rgba(255,255,255,0.5)"
                  />
                  <use
                    href="#gentle-wave"
                    x="48"
                    y="5"
                    fill="rgba(255,255,255,0.3)"
                  />
                  <use href="#gentle-wave" x="48" y="7" fill="#fff" />
                </g>
              </svg>
        </div>
    </>
  );
}

ResetPassword.propTypes = {
  loading: PropTypes.bool,
  deleteSuccessful: PropTypes.bool,
  changeResetPassword: PropTypes.func,
  deleteResetPassword: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  loading: selectResetPasswordLoading(),
  error: selectResetPasswordError(),
  deleteSuccessful: selectResetPasswordDeleteSuccessful(),
  resetPasswordList: selectResetPasswordList(),
  resetPasswordData: selectResetPasswordData(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateResetPassword()),
    loadResetPassword: id => dispatch(loadResetPassword(id)),
    changeResetPassword: data => dispatch(changeResetPassword(data)),
    deleteResetPassword: id => dispatch(deleteResetPassword(id)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(ResetPassword);
